import * as React from 'react';
import { forwardRef, useCallback } from 'react';
import clsx from 'clsx';
import { NavLink, Link } from 'react-router-dom';
import { AppBar, Box, Toolbar, IconButton, Typography, Menu, Container, Button, MenuItem, Tooltip, Switch, FormControlLabel } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import SettingsIcon from '@mui/icons-material/Settings';
import makeStyles from '@mui/styles/makeStyles';
import { AppButton } from './AppButton';
import { WalletMultiButton } from "@solana/wallet-adapter-react-ui";
import { useAppStore } from '../store/AppStore';

// const pages = ['Pending Bets', 'About Us', 'How it Works', 'Docs', 'Sports', 'Home'];

const pages = [
    { title: 'Go to Purebet on Solana', link: 'https://exchange.purebet.io' },
    { title: 'Home', link: '/' },
    { title: 'Sports', link: '/sports' },
    { title: 'Bet History', link: '/bet-history'},
    { title: 'Leaderboard', link: '/leaderboard' },
    { title: 'Docs', link: '/docs' },
];

const pages_reverse = pages.reverse();

const useStyles = makeStyles((theme) => ({
    background: {
        backgroundColor: theme.palette.background.default,
        backgroundImage: 'none'
    },
    wallet: {
        // color: 'white', 
        backgroundColor: theme.palette.primary.main,
        // my: 2,
        // display: 'block'
        lineHeight: '1.2'
    },
    root: {
        // flexGrow: 1, // takes all width
    },
    link: {
        // Set default color of the AppBar link here
        // color: theme.palette.text.secondary, // Smooth text
        // color: theme.palette.text.primary, // Contrast text
        color: theme.palette.text.color
    },
    linkActive: {
        // Applied to the AppBar link when "associated page" is selected
        // color: theme.palette.text.primary, // Contrast text
        color: theme.palette.primary.main, // Colorful as "primary"
        // color: theme.palette.secondary.main, // Colorful as "secondary"
    },
    button: {
        // color: theme.palette.button,
        // padding: '10px 8px',
        justifyContent: 'center',
        // textTransform: 'none',
        // letterSpacing: 0,
        width: '100%',
        // // fontWeight: theme.typography.fontWeightMedium,
        // flexGrow: 1,
        my: 2,
        // color: 'white',
        display: 'flex'
    },
}));

/**
 * Router link with styling to use in AppBar, highlights the current url
 * @component AppBarLink
 */
const AppBarLink = forwardRef(({ className, ...restOfProps }, ref) => {
    const classes = useStyles();
    const classLink = clsx(className, classes.link);
    return (
        <div ref={ref} className={classes.root}>
            <NavLink
                className={({ isActive }) => classLink + (isActive ? ` ${classes.linkActive}` : '')}
                {...restOfProps}
            />
        </div>
    );
});

function ResponsiveAppBar() {
    const [anchorElNav, setAnchorElNav] = React.useState(null);
    const [anchorElUser, setAnchorElUser] = React.useState(null);
    const [state, dispatch] = useAppStore();

    const handleOpenNavMenu = (event) => {
        setAnchorElNav(event.currentTarget);
    };
    const handleOpenUserMenu = (event) => {
        setAnchorElUser(event.currentTarget);
    };

    const handleCloseNavMenu = () => {
        setAnchorElNav(null);
    };

    const handleCloseUserMenu = () => {
        setAnchorElUser(null);
    };

    const handleMenuItem = () => {
        dispatch({
            type: 'SET_DISP_MARKET',
            isDispMarket: false
        });
        //console.log("ResponsiveAppBar.js is running handleMenuItem");
        dispatch({
            type: "SET_URL",
            paramStr: "",
            signal: "noparam"
        })
    };
    const handleMenuItemExtra = (link) => {
        handleMenuItem();
        window.open(link, "_self");
    }

    // const handleDocItem = () => {
    //     window.open('https://purebetprotocol.gitbook.io/purebet', '_blank');
    // };

    // const handleHowItem = () => {
    //     window.open('https://purebet.medium.com/how-purebet-works-61fc02d1dda9', '_blank');
    // };

    // const handleAboutItem = () => {
    //     window.open('https://purebet.medium.com/about-purebet-db759a42aba7', '_blank');
    // };

    const classes = useStyles();

    const handleSwitchDarkMode = useCallback(() => {
        dispatch({
            type: 'SET_DARK_MODE',
            darkMode: !state.darkMode,
            payload: !state.darkMode,
        });
    }, [state, dispatch]);

    const handleSwitchSoccerMode = useCallback(() => {
        dispatch({
            type: 'SET_SOCCER_MODE',
            soccerMode: !state.soccerMode
        });
    }, [state, dispatch]);

    const handleBetSlipStyle = useCallback(() => {
        dispatch({
            type: 'SET_LAYBET_SLIP_STYLE',
            layBetSlipStyle: !state.layBetSlipStyle
        });
    }, [state, dispatch]);

    const handleOddsStyle = useCallback(() => {
        dispatch({
            type: 'SET_ODDS_STYLE',
            oddsStyle: !state.oddsStyle
        });
    }, [state, dispatch]);

    return (
        <AppBar position="fixed" className={classes.background}>
            <Container maxWidth="false">
                <Toolbar disableGutters sx={{ justifyContent: 'space-between' }}>
                    <Link to="/">
                    <Box
                        component="img"
                        sx={{
                            height: 100,
                            display: { xs: 'none', md: 'flex', marginTop: "5px", marginBottom: "5px" }
                        }}
                        alt="logo"
                        src={state.darkMode ? '/img/logo_dark.png' : '/img/logo_light.png'}
                    />
                    </Link>
                    <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
                        <IconButton
                            size="large"
                            aria-label="account of current user"
                            aria-controls="menu-appbar"
                            aria-haspopup="true"
                            onClick={handleOpenNavMenu}
                            color="inherit"
                        >
                            <MenuIcon />
                        </IconButton>
                        <Menu
                            id="menu-appbar"
                            anchorEl={anchorElNav}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'left',
                            }}
                            keepMounted
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'left',
                            }}
                            open={Boolean(anchorElNav)}
                            onClose={handleCloseNavMenu}
                            sx={{
                                display: { xs: 'block', md: 'none' },
                            }}
                        >
                            {pages.map((page) => (
                                page.title === 'Docs' ? (
                                    <MenuItem key={page.title} onClick={() => window.open("https://docs.purebet.io", "_blank")}>
                                        <Typography textAlign="center">{page.title}</Typography>
                                    </MenuItem>
                                ) : page.title === 'How it Works' ? (
                                    <MenuItem key={page.title} onClick={() => window.open("https://www.purebet.io/how-it-works.html", "_self")}>
                                        <Typography textAlign="center">{page.title}</Typography>
                                    </MenuItem>
                                ) : page.title === 'About Us' ? (
                                    <MenuItem key={page.title} onClick={() => window.open("https://www.purebet.io/about.html", "_self")}>
                                        <Typography textAlign="center">{page.title}</Typography>
                                    </MenuItem>
                                // ) : page.title === 'Onboarding' ? (
                                //     <MenuItem key={page.title} onClick={() => window.open("https://www.purebet.io/onboard.html", "_blank")}>
                                //         <Typography textAlign="center">{page.title}</Typography>
                                //     </MenuItem>
                                ) : (
                                    <MenuItem key={page.title} onClick={() => handleMenuItemExtra(page.link)}>
                                        <Typography textAlign="center">{page.title}</Typography>
                                    </MenuItem>
                                )
                            ))}
                        </Menu>
                    </Box>

                    <Box
                        component="img"
                        sx={{
                            height: 100,
                            display: { xs: 'flex', md: 'none' }
                        }}
                        alt="logo"
                        src={state.darkMode ? '/img/logo_dark.png' : '/img/logo_light.png'}
                    />

                    <Box sx={{ flexGrow: 1, flexDirection: 'row-reverse', display: { xs: 'none', md: 'flex' } }}>
                        {pages_reverse.map((page) => (
                            page.title === 'Docs' ? (
                                <Button key={page.title} onClick={() => window.open("https://docs.purebet.io", "_blank")} sx={{ color: state.darkMode ?'white':'black' }}>
                                    {page.title}
                                </Button>
                            ) : page.title === 'How it Works' ? (
                                <Button key={page.title} onClick={() => window.open("https://www.purebet.io/how-it-works.html", "_self")} sx={{ color: state.darkMode ?'white':'black' }}>
                                    {page.title}
                                </Button>
                            ) : page.title === 'About Us' ? (
                                <Button key={page.title} onClick={() => window.open("https://www.purebet.io/about.html", "_self")} sx={{ color: state.darkMode ?'white':'black' }}>
                                    {page.title}
                                </Button>
                            // ) : page.title === 'Onboarding' ? (
                            //     <Button key={page.title} onClick={() => window.open("https://www.purebet.io/onboard.html", "_blank")} sx={{ color: state.darkMode ?'white':'black' }}>
                            //         {page.title}
                            //     </Button>
                            ) : page.title === 'Go to Purebet on Solana' ? (
                                <Button key={page.title} onClick={() => window.open("https://exchange.purebet.io", "_self")} sx={{ color: state.darkMode ?'white':'black' }}>
                                    {page.title}
                                </Button>
                            ) : (
                                <Button className={classes.button} component={AppBarLink} to={page.link} key={page.title} onClick={handleMenuItem}>
                                    {page.title}
                                </Button>
                            )
                        ))}
                    </Box>

                    <Box sx={{ flexGrow: 0, padding: 1, marginLeft: 1, marginRight: 1 }}>
                        <Tooltip title="Open settings">
                            <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                                <SettingsIcon />
                            </IconButton>
                        </Tooltip>
                        <Menu
                            sx={{ mt: '45px' }}
                            id="menu-appbar"
                            anchorEl={anchorElUser}
                            anchorOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            keepMounted
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            open={Boolean(anchorElUser)}
                            onClose={handleCloseUserMenu}
                        >
                            <MenuItem key="1" onClick={handleCloseUserMenu}>
                                <Tooltip title={state.darkMode ? 'Switch to Light mode' : 'Switch to Dark mode'}>
                                    <FormControlLabel
                                        label={!state.darkMode ? 'Light mode' : 'Dark mode'}
                                        control={<Switch color="primary" checked={state.darkMode} onChange={handleSwitchDarkMode} />}
                                    />
                                </Tooltip>
                            </MenuItem>
                            <MenuItem key="2" onClick={handleCloseUserMenu}>
                                <Tooltip title={state.layBetSlipStyle ? 'Switch to risk-based odds/stakes' : 'Switch to traditional lay odds/stakes'}>
                                    <FormControlLabel
                                        label={state.layBetSlipStyle ? "Lays as traditional format" : 'Lays as risk'}
                                        control={<Switch color="primary" checked={state.layBetSlipStyle} onChange={handleBetSlipStyle} />}
                                    />
                                </Tooltip>
                            </MenuItem>
                            <MenuItem key="3" onClick={handleCloseUserMenu}>
                                <Tooltip title={state.soccerMode ? 'Switch to football mode' : 'Switch to soccer mode'}>
                                    <FormControlLabel
                                        label={state.soccerMode ? 'Soccer mode' : 'Football mode'}
                                        control={<Switch color="primary" checked={state.soccerMode} onChange={handleSwitchSoccerMode} />}
                                    />
                                </Tooltip>
                            </MenuItem>
                            <MenuItem key="4" onClick={handleCloseUserMenu}>
                                <Tooltip title={state.oddsStyle ? 'Switch to american odds' : 'Switch to decimal odds'}>
                                    <FormControlLabel
                                        label={state.oddsStyle ? 'Decimal odds' : 'American odds'}
                                        control={<Switch color="primary" checked={state.oddsStyle} onChange={handleOddsStyle} />}
                                    />
                                </Tooltip>
                            </MenuItem>
                        </Menu>
                    </Box>

                    <Box sx={{ flexGrow: 0 }}>
                        <WalletMultiButton className={classes.wallet} />
                    </Box>

                </Toolbar>
            </Container>
        </AppBar>
    );
}
export default ResponsiveAppBar;